"use client"

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { Calendar } from "lucide-react"
import { format } from "date-fns"
import { fr } from "date-fns/locale"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"

const formSchema = z.object({
  clientName: z.string().min(2, {
    message: "Le nom du client doit contenir au moins 2 caractères.",
  }),
  clientEmail: z.string().email({
    message: "Veuillez entrer une adresse email valide.",
  }).optional().or(z.literal("")),
  clientPhone: z.string().min(10, {
    message: "Veuillez entrer un numéro de téléphone valide.",
  }).optional().or(z.literal("")),
  sendType: z.enum(["now", "scheduled"], {
    required_error: "Veuillez choisir quand envoyer le code.",
  }),
  scheduledFor: z.date().optional(),
})

interface GenerateCodeFormProps {
  keyId: string
  keyName: string
  onSuccess: () => void
  onCancel: () => void
}

export function GenerateCodeForm({ keyId, keyName, onSuccess, onCancel }: GenerateCodeFormProps) {
  const { toast } = useToast()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      clientName: "",
      clientEmail: "",
      clientPhone: "",
      sendType: "now",
      scheduledFor: undefined,
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    console.log("Submitting form with values:", values)
    try {
      // Vérifier que l'utilisateur est authentifié
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      if (sessionError || !session) {
        console.error("Authentication error:", sessionError)
        toast({
          title: "Erreur d'authentification",
          description: "Veuillez vous connecter pour générer un code",
          variant: "destructive",
        })
        return
      }

      // Vérifier si la clé existe dans keynest_keys
      console.log("Checking if key exists in keynest_keys...")
      const { data: existingKey, error: keyError } = await supabase
        .from('keynest_keys')
        .select()
        .eq('key_id', keyId)
        .maybeSingle()

      if (!existingKey) {
        console.log("Key not found, inserting into keynest_keys...")
        const { error: insertError } = await supabase
          .from('keynest_keys')
          .insert({
            key_id: keyId,
            key_name: keyName,
            status: 'available'
          })

        if (insertError) {
          console.error("Error inserting key:", insertError)
          throw insertError
        }
      }

      // Générer le code via l'API Keynest
      console.log("Requesting Keynest code...")
      const { data: keynestResponse, error: keynestError } = await supabase.functions.invoke('get-keynest-code', {
        body: {
          keyId: keyId,
          clientName: values.clientName
        }
      })

      if (keynestError) {
        console.error("Error getting Keynest code:", keynestError)
        throw keynestError
      }

      console.log("Keynest response received:", keynestResponse)

      if (!keynestResponse?.ResponsePacket?.CollectionCode) {
        throw new Error("No code received from Keynest")
      }

      const code = keynestResponse.ResponsePacket.CollectionCode
      console.log("Keynest code extracted:", code)

      // Sauvegarder le code dans la base de données avec la logique corrigée
      const { data: codeData, error: codeError } = await supabase
        .from('collection_codes')
        .insert({
          key_id: keyId,
          key_name: keyName,
          code: code,
          client_name: values.clientName,
          client_email: values.clientEmail || null,
          client_phone: values.clientPhone || null,
          status: 'pending',
          scheduled_for: values.sendType === "scheduled" ? values.scheduledFor?.toISOString() : null,
          // Important: Ne pas définir sent_at lors de la création
          sent_at: values.sendType === "now" ? new Date().toISOString() : null
        })
        .select()
        .single()

      if (codeError) {
        console.error("Error saving code:", codeError)
        throw codeError
      }

      console.log("Code saved successfully:", codeData)

      let notificationsSent = true;
      let emailError = null;
      let smsError = null;

      // Si envoi immédiat, envoyer les notifications
      if (values.sendType === "now") {
        // Envoyer le code par email si une adresse est fournie
        if (values.clientEmail) {
          console.log("Sending code by email...")
          const { error } = await supabase.functions.invoke('send-code-email', {
            body: {
              clientName: values.clientName,
              clientEmail: values.clientEmail,
              code: code,
              keyName: keyName
            }
          })

          if (error) {
            console.error("Error sending email:", error)
            emailError = error;
            notificationsSent = false;
          } else {
            console.log("Email sent successfully")
          }
        }

        // Envoyer le code par SMS si un numéro est fourni
        if (values.clientPhone) {
          console.log("Sending code by SMS...")
          const { error } = await supabase.functions.invoke('send-code-sms', {
            body: {
              clientName: values.clientName,
              clientPhone: values.clientPhone,
              code: code,
              keyName: keyName
            }
          })

          if (error) {
            console.error("Error sending SMS:", error)
            smsError = error;
            notificationsSent = false;
          } else {
            console.log("SMS sent successfully")
          }
        }

        // Mettre à jour le statut si les notifications ont été envoyées avec succès
        if (notificationsSent) {
          console.log("Updating code status to sent...")
          const { error: updateError } = await supabase
            .from('collection_codes')
            .update({ 
              status: 'sent'
            })
            .eq('id', codeData.id)

          if (updateError) {
            console.error("Error updating code status:", updateError)
          } else {
            console.log("Code status updated successfully")
          }
        }

        // Afficher les erreurs d'envoi si nécessaire
        if (emailError) {
          toast({
            title: "Erreur lors de l'envoi de l'email",
            description: "Le code a été généré mais l'email n'a pas pu être envoyé.",
            variant: "destructive",
          })
        }
        if (smsError) {
          toast({
            title: "Erreur lors de l'envoi du SMS",
            description: "Le code a été généré mais le SMS n'a pas pu être envoyé.",
            variant: "destructive",
          })
        }
      }

      toast({
        title: "Code généré avec succès",
        description: values.sendType === "now" 
          ? `Un code a été généré et envoyé pour la clé ${keyName}`
          : `Un code a été généré et sera envoyé le ${format(values.scheduledFor!, "PPP", { locale: fr })} pour la clé ${keyName}`,
      })

      onSuccess()
    } catch (error) {
      console.error("Error generating code:", error)
      toast({
        title: "Erreur",
        description: "Impossible de générer le code",
        variant: "destructive",
      })
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="clientName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nom du client</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="clientEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email du client</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormDescription>
                L'email est optionnel mais recommandé pour l'envoi du code
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="clientPhone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Téléphone du client</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+33 6 12 34 56 78" {...field} />
              </FormControl>
              <FormDescription>
                Le téléphone est optionnel mais recommandé pour l'envoi du code
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="sendType"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Quand envoyer le code ?</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col space-y-1"
                >
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="now" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      Envoyer maintenant
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="scheduled" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      Programmer l'envoi
                    </FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {form.watch("sendType") === "scheduled" && (
          <FormField
            control={form.control}
            name="scheduledFor"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Date d'envoi</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-[240px] pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP", { locale: fr })
                        ) : (
                          <span>Choisir une date</span>
                        )}
                        <Calendar className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button type="submit">Générer le code</Button>
        </div>
      </form>
    </Form>
  )
}